import type { Match, Badge } from '@types';

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 0,
});

export const currencyFormatterWithCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 2,
});

export const decimalFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  maximumFractionDigits: 0,
});

function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const convertToCurrency = (amount: number): string => {
  let result = '$0';
  if (Math.round((amount + Number.EPSILON) * 100) / 100 != 0) {
    result = currencyFormatter.format(amount);
  }
  return result;
};

export const convertToCurrencyWithCents = (amount: number): string => {
  let result = '$0';
  if (Math.round((amount + Number.EPSILON) * 100) / 100 != 0) {
    result = currencyFormatterWithCents.format(amount);
  }
  return result;
};

export const convertToDecimal = (amount: number): string => {
  let result = '0';
  if (Math.round((amount + Number.EPSILON) * 100) / 100 != 0) {
    result = decimalFormatter.format(amount);
  }
  return result;
};

export const centsToDisplayDollars = (cents: number): string => {
  if (!Number.isFinite(cents) || cents <= 0) return '0';
  const dollars = Math.floor(cents / 100);
  return currencyFormatter.format(dollars);
};

export const outputCentsToDollars = (cents: number): string => {
  if (!Number.isFinite(cents) || cents <= 0) return '0';
  const dollars = Math.floor(cents / 100);
  return decimalFormatter.format(dollars);
};

export const inputDollarsToCents = (raw: string): number => {
  const digitsOnly = raw.replace(/[^\d]/g, '');
  if (!digitsOnly) return 0;
  const dollars = parseInt(digitsOnly, 10);
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
};

export const processImage = async (file: File, width: number, height: number): Promise<File> => {
  const img = await fileToImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    ctx.drawImage(img, 0, 0, width, height);
  }

  const webpBlob: Blob = await new Promise((resolve) =>
    canvas.toBlob((blob) => resolve(blob as Blob), 'image/webp', 0.9),
  );

  return new File([webpBlob], file.name.replace(/\.(jpg|jpeg|png)$/i, '.webp'), {
    type: 'image/webp',
  });
};

export function waitForElement(selector: string, timeoutMs = 15000): Promise<HTMLElement> {
  const immediate = document.querySelector(selector) as HTMLElement | null;
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve, reject) => {
    const root = document.documentElement;
    const obs = new MutationObserver(() => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (el) {
        obs.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    });
    obs.observe(root, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      obs.disconnect();
      reject(new Error(`Timeout waiting for selector: ${selector}`));
    }, timeoutMs);
  });
}

export function getMatchCount(matches: Match[]): number {
  const global =
    matches.filter((match) => {
      return match.categories.length == 0;
    }).length > 0
      ? 1
      : 0;
  const category =
    matches.filter((match) => {
      return match.categories.length > 0;
    }).length > 0
      ? 1
      : 0;
  return global + category;
}

export function sortBadgesByMostRecent(badges: Badge[]): Badge[] {
  return [...badges].sort((a: Badge, b: Badge) => b.year - a.year || b.month - a.month);
}

export function normalizeUrl(url: string): string {
  if (!url) return url;

  let candidate = url.trim();

  if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(candidate)) {
    candidate = `https://${candidate}`;
  }

  try {
    new URL(candidate);
    return candidate;
  } catch {
    return url;
  }
}

export function isValidEmail(email: string): boolean {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
